import React, { useMemo, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import { grey } from '@material-ui/core/colors';
import { IconButton, Tooltip as MuiTooltip, Tooltip, TableContainer, Table, TableHead, TableCell, TableRow, TableBody, Checkbox, withStyles } from '@material-ui/core';
import { FaFilter } from 'react-icons/fa6';
import { MdBarChart } from 'react-icons/md';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';
import ModelValidationParamHistoryPopup from './ModelValidationParamHistoryPopup';

const DIFF_EPS = 1e-6, MAX_DEFAULT = 25;
const FIELD_HINTS = new Set(['value', 'true_value', 'estimated_value', 'ae', 'se', 'ape', 'tolerance', 'accurate']);
const COLUMN_LABELS = { variable: 'Attribute', value: 'Value', uom: 'UOM', true_value: 'True', estimated_value: 'Est', ae: 'Ae', se: 'Se', ape: 'Ape', tolerance: 'Tol', accurate: 'Acc' };

const CustomCheckbox = withStyles({ root: { color: grey[100], '&$checked': { color: grey[200] } }, checked: {} })((props) => <Checkbox color="default" {...props} />);

function isNumber(x) { return typeof x === 'number' && Number.isFinite(x); }
function isPrimitive(x) { return x == null || (typeof x !== 'object'); }
function looksLikeFieldSet(obj) { return !!obj && typeof obj === 'object' && Object.keys(obj).some(k => FIELD_HINTS.has(k)); }
function rootFromPath(path) { return String(path || '').split(/[.\[]/)[0]; }

function buildUomMap(cleanMeasurementData) {
    const m = new Map(), fields = cleanMeasurementData?.fields || [];
    for (const f of fields) {
        if (!f?.name) continue;
        const name = String(f.name);
        m.set(name, f.uom ?? null);
        if (Array.isArray(f.components)) for (const c of f.components) if (c?.name) m.set(name + '.' + String(c.name), f.uom ?? null);
    }
    return m;
}

function buildRowsFromFeatures(features, uomMap) {
    const rows = [];
    const hasUoms = !!uomMap && typeof uomMap.get === 'function' && uomMap.size > 0;

    const getUom = (path) => {
        if (!hasUoms) return undefined;
        const p = String(path || '');
        if (uomMap.has(p)) return uomMap.get(p) ?? null;
        return uomMap.get(rootFromPath(p)) ?? null;
    };

    const walk = (prefix, val) => {
        if (isPrimitive(val)) { const u = getUom(prefix); rows.push({ variable: prefix, fields: hasUoms ? { value: val, uom: u } : { value: val } }); return; }
        if (Array.isArray(val)) { val.forEach((item, i) => walk(`${prefix}[${i}]`, item)); return; }
        if (looksLikeFieldSet(val)) { const u = getUom(prefix); rows.push({ variable: prefix, fields: hasUoms ? { ...val, uom: u } : val }); return; }
        for (const [k, v] of Object.entries(val || {})) walk(prefix ? `${prefix}.${k}` : k, v);
    };

    for (const [k, v] of Object.entries(features || {})) walk(k, v);
    return rows;
}

function buildColumns(rows) {
    const known = ['variable', 'value', 'uom', 'true_value', 'estimated_value', 'ae', 'se', 'ape', 'tolerance', 'accurate'];
    const present = new Set(); rows.forEach(r => Object.keys(r.fields || {}).forEach(c => present.add(c)));
    const others = Array.from(present).filter(c => !known.includes(c)).sort((a, b) => a.localeCompare(b));
    return ['variable', ...known.filter(c => c !== 'variable' && present.has(c)), ...others, '__history'];
}

function fmt(v) {
    if (v == null || Number.isNaN(v)) return '-';
    if (typeof v === 'number') { const a = Math.abs(v); if (a >= 100) return v.toFixed(1); if (a >= 1) return v.toFixed(2); return v.toFixed(3); }
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
}

function truncateUom(uom, max = 5, head = 3) {
    const s = uom == null ? '' : String(uom);
    if (!s) return '-';
    if (s.length <= max) return s;
    return s.slice(0, head) + '...';
}

function renderUomCell(v) {
    const raw = v == null ? '' : String(v);
    const shown = truncateUom(raw);
    if (!raw || raw === '-' || raw.length <= 5) return shown;
    return (<Tooltip title={raw} arrow><span>{shown}</span></Tooltip>);
}

function hasTolerance(fields) { return fields && Object.prototype.hasOwnProperty.call(fields, 'tolerance') && fields.tolerance != null; }
function rowHighlight(fields) { if (fields?.accurate === true) return 'bg-green-50'; if (fields?.accurate === false) return 'bg-red-50'; return ''; }

export default function ModelValidationDetailsSidebar() {
    const { modelValidationLogDataList, modelValidationLogDataListByTs, cleanMeasurementData } = useOptimizationPipeline();

    const [q, setQ] = useState('');
    const [differenceFilter, setDifferenceFilter] = useState(false);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupSeedKeys, setPopupSeedKeys] = useState([]);

    const features = modelValidationLogDataListByTs?.features || null;
    const uomMap = useMemo(() => buildUomMap(cleanMeasurementData), [cleanMeasurementData]);

    const availableKeys = useMemo(() => {
        const keys = features ? Object.keys(features).filter(Boolean) : [];
        return keys.sort((a, b) => a.localeCompare(b, 'hu'));
    }, [features]);

    const rowsAll = useMemo(() => buildRowsFromFeatures(features, uomMap), [features, uomMap]);
    const columns = useMemo(() => buildColumns(rowsAll), [rowsAll]);

    const relevantKeys = useMemo(() => rowsAll.filter(r => hasTolerance(r.fields || {})).map(r => r.variable).filter(Boolean).slice(0, MAX_DEFAULT), [rowsAll]);

    const defaultDiffKeys = useMemo(() => {
        if (!features) return [];
        const keys = [];
        for (const k of availableKeys) {
            const f = features[k], a = f?.true_value, b = f?.estimated_value;
            if (isNumber(a) && isNumber(b) && Math.abs(a - b) > DIFF_EPS) keys.push(k);
            if (keys.length >= MAX_DEFAULT) break;
        }
        return keys;
    }, [features, availableKeys]);

    const openTimeseries = (seedKeys) => { setPopupSeedKeys(Array.isArray(seedKeys) ? seedKeys : []); setPopupOpen(true); };

    const rows = useMemo(() => {
        let list = rowsAll;
        if (q) { const needle = q.toLowerCase(); list = list.filter(r => r.variable.toLowerCase().includes(needle) || Object.values(r.fields || {}).some(v => String(v).toLowerCase().includes(needle))); }
        if (differenceFilter) list = list.filter(r => hasTolerance(r.fields || {}));
        return list;
    }, [rowsAll, q, differenceFilter]);

    const headerSeed = relevantKeys.length ? relevantKeys : defaultDiffKeys;

    return (<>
        <TableContainer component={Paper}>
            <div className="w-full flex items-center justify-between bg-black text-white p-6">
                <div className="font-medium flex gap-12 items-center ml-12"><FaFilter className="opacity-80" /><span>Model Validation</span></div>

                <div className="flex items-center gap-12">
                    <MuiTooltip title="Open feature timeseries (preselect relevant)" arrow><span>
                        <IconButton size="small" onClick={() => openTimeseries(headerSeed)} style={{ color: 'white' }} disabled={!availableKeys.length}><MdBarChart size={20} /></IconButton>
                    </span></MuiTooltip>

                    <input className="border border-white border-opacity-20 bg-transparent rounded px-6 py-3 text-xs outline-none focus:ring-2 focus:ring-white ring-opacity-20 placeholder-white" placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} />

                    <div className="flex items-center gap-2">
                        <div className="text-xs opacity-50">Show relevant</div>
                        <CustomCheckbox checked={differenceFilter} onChange={() => setDifferenceFilter(!differenceFilter)} />
                    </div>
                </div>
            </div>

            <Table className="w-full shadow-2xl">
                <TableHead><TableRow>
                    {columns.map(col => (
                        <TableCell key={col} align={col === 'variable' ? 'left' : (col === '__history' ? 'center' : 'right')} className="text-xs" style={col === 'variable' ? { width: 1 } : undefined}>
                            {col === '__history' ? '' : (COLUMN_LABELS[col] || col)}
                        </TableCell>
                    ))}
                </TableRow></TableHead>

                <TableBody>
                    {rows.map((r, idx) => (
                        <TableRow key={r.variable} className={`${(idx + 1) % 2 === 1 ? 'bg-gray-100' : ''} ${rowHighlight(r.fields)}`}>
                            {columns.map(col => col === 'variable'
                                ? (<TableCell key={col} className="p-0"><Tooltip title={r.variable} arrow><div className="whitespace-nowrap font-mono text-xs px-16 py-12">{r.variable}</div></Tooltip></TableCell>)
                                : col === '__history'
                                    ? (<TableCell key={col} align="center"><MuiTooltip title="Open timeseries" arrow><span><IconButton size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openTimeseries([r.variable]); }}><MdBarChart size={18} /></IconButton></span></MuiTooltip></TableCell>)
                                    : (<TableCell key={col} align="right" className="whitespace-nowrap tabular-nums text-xs">{col === 'uom' ? renderUomCell(r.fields?.[col]) : fmt(r.fields?.[col])}</TableCell>)
                            )}
                        </TableRow>
                    ))}

                    {rows.length === 0 && (<TableRow><TableCell colSpan={columns.length} align="center" className="py-16 opacity-60 text-xs">No data available.</TableCell></TableRow>)}
                </TableBody>
            </Table>
        </TableContainer>

        <ModelValidationParamHistoryPopup open={popupOpen} onClose={() => setPopupOpen(false)} logDataList={modelValidationLogDataList} availableKeys={availableKeys} uomMap={uomMap} defaultSelectedKeys={popupSeedKeys} />
    </>);
}